/**
 * Reads an operator's brand colours out of their logo's pixels.
 *
 * Pure on purpose: it takes RGBA pixels, not an image, so it is tested in node
 * without a canvas. The browser half (file -> pixels) is modules/appearance/image.ts.
 *
 * The rules come from measuring a real logo (Lotus Shedune, 2026-09-12: navy
 * #011C4E 80.5% and gold #ECA809 18.0% of chromatic pixels, on white) and are
 * recorded in radius-wiki portal-brand-from-logo.md:
 *
 * - 🔑 SPLIT BY HUE, NOT BY FREQUENCY. Near-neutral pixels (s <= 0.25) and the
 *   extremes (v < 0.10, v > 0.97) are dropped first, or white background wins
 *   every logo.
 * - Hue families are 15° bins smoothed with their neighbours, so a colour whose
 *   hue sits on a bin edge is still one family.
 * - Each family's colour is its most common quantised colour, not its mean:
 *   anti-aliased edges would otherwise wash the gold out toward the background.
 * - 🔑 THE GROUND IS SYNTHESISED, NEVER SCRAPED. A JPEG's white is compression
 *   noise (Lotus's reads warm #F5F4ED only because of ringing around the gold).
 * - A logo with one colour family gets a DERIVED accent, and says so.
 */

export interface BrandColours {
  /** 30: the colour that writes. */
  ink: string;
  /** 10: the colour that fills. */
  accent: string;
  /** 60: a near-white tinted toward the ink. */
  ground: string;
  /** The accent was not found in the logo; it is a suggested complement. */
  accentDerived: boolean;
  /** False when the logo has no colour at all (black, grey or white only). */
  chromatic: boolean;
  /**
   * Every brand colour family found in the logo, largest first, at most
   * MAX_COLOURS. Starts with the ink, then the accent when it was found. Empty
   * for a colourless logo. Variations draw their alternative accents from here.
   */
  colours: string[];
}

export const DEFAULT_INK = "#1B2A41";
export const DEFAULT_ACCENT = "#E3A02C";

const NEUTRAL_SATURATION = 0.25;
const MIN_VALUE = 0.1;
const MAX_VALUE = 0.97;
const BINS = 24; // 15° each
const ACCENT_MIN_DEGREES = 60;
/** A second family smaller than this is anti-aliasing noise, not a brand colour. */
const ACCENT_MIN_SHARE = 0.03;
/** Families closer than this are shades of one colour, not two brand colours. */
const FAMILY_MIN_DEGREES = 45;
const MAX_COLOURS = 4;

interface Bucket { n: number; r: number; g: number; b: number }

function hsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);
  let h = 0;
  if (d > 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

function hex(r: number, g: number, b: number): string {
  return "#" + [r, g, b].map((x) => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, "0")).join("").toUpperCase();
}

/** `#RRGGBB` as hue (0-360), saturation and value (0-1). */
export function hsvOf(hexColour: string): { h: number; s: number; v: number } {
  return hsv(
    parseInt(hexColour.slice(1, 3), 16) / 255,
    parseInt(hexColour.slice(3, 5), 16) / 255,
    parseInt(hexColour.slice(5, 7), 16) / 255,
  );
}

export function fromHsv(h: number, s: number, v: number): string {
  h = ((h % 360) + 360) % 360;
  s = Math.max(0, Math.min(1, s));
  v = Math.max(0, Math.min(1, v));
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return hex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

function luminance(hexColour: string): number {
  const lin = (i: number) => {
    const s = parseInt(hexColour.slice(i, i + 2), 16) / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(1) + 0.7152 * lin(3) + 0.0722 * lin(5);
}

const circular = (a: number, b: number) => {
  const d = Math.abs(a - b) % 360;
  return Math.min(d, 360 - d);
};

/** The most common quantised colour across a set of buckets, as its mean. */
function dominant(maps: Array<Map<number, Bucket>>): string | null {
  const merged = new Map<number, Bucket>();
  for (const m of maps) {
    for (const [k, v] of m) {
      const t = merged.get(k) ?? { n: 0, r: 0, g: 0, b: 0 };
      t.n += v.n; t.r += v.r; t.g += v.g; t.b += v.b;
      merged.set(k, t);
    }
  }
  let best: Bucket | null = null;
  for (const v of merged.values()) if (!best || v.n > best.n) best = v;
  return best ? hex(best.r / best.n, best.g / best.n, best.b / best.n) : null;
}

function add(map: Map<number, Bucket>, r: number, g: number, b: number) {
  const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
  const t = map.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
  t.n++; t.r += r; t.g += g; t.b += b;
  map.set(key, t);
}

/** A near-white at the ink's hue. A neutral ink gets a neutral ground. */
export function groundFor(ink: string): string {
  const c = hsvOf(ink);
  return c.s < 0.1 ? "#F5F5F5" : fromHsv(c.h, 0.025, 0.976);
}

/**
 * The brand colours in `data` (RGBA, width × height). Pixels under 50% alpha
 * are ignored: a transparent PNG's hidden colour is not part of the brand.
 */
export function extractBrand(data: ArrayLike<number>, width: number, height: number): BrandColours {
  const counts = new Array<number>(BINS).fill(0);
  const buckets = Array.from({ length: BINS }, () => new Map<number, Bucket>());
  const darkNeutrals = new Map<number, Bucket>();
  let chromaticTotal = 0;

  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    if ((data[o + 3] ?? 0) < 128) continue;
    const r = data[o] ?? 0, g = data[o + 1] ?? 0, b = data[o + 2] ?? 0;
    const c = hsv(r / 255, g / 255, b / 255);
    if (c.s <= NEUTRAL_SATURATION || c.v < MIN_VALUE || c.v > MAX_VALUE) {
      if (c.v < 0.6) add(darkNeutrals, r, g, b);
      continue;
    }
    const bin = Math.floor(c.h / 15) % BINS;
    counts[bin]!++;
    add(buckets[bin]!, r, g, b);
    chromaticTotal++;
  }

  if (chromaticTotal === 0) {
    const ink = dominant([darkNeutrals]) ?? DEFAULT_INK;
    return { ink, accent: DEFAULT_ACCENT, ground: groundFor(ink), accentDerived: true, chromatic: false, colours: [] };
  }

  const at = (k: number) => counts[(k + BINS) % BINS]!;
  const smoothed = counts.map((_, k) => at(k - 1) + at(k) + at(k + 1));
  const family = (k: number) => [k - 1, k, k + 1].map((j) => buckets[(j + BINS) % BINS]!);
  const centre = (k: number) => k * 15 + 7.5;

  let first = 0;
  for (let k = 1; k < BINS; k++) if (smoothed[k]! > smoothed[first]!) first = k;

  let second = -1;
  for (let k = 0; k < BINS; k++) {
    if (circular(centre(k), centre(first)) < ACCENT_MIN_DEGREES) continue;
    if (second < 0 || smoothed[k]! > smoothed[second]!) second = k;
  }
  const secondIsBrand = second >= 0 && smoothed[second]! / chromaticTotal >= ACCENT_MIN_SHARE;

  // Every other family big enough to be brand, greedily by size. The first and
  // second are chosen above by their own (stricter) rule and lead the list.
  const peaks = secondIsBrand ? [first, second] : [first];
  const bySize = counts.map((_, k) => k).sort((a, b) => smoothed[b]! - smoothed[a]! || a - b);
  for (const k of bySize) {
    if (peaks.length >= MAX_COLOURS || smoothed[k]! / chromaticTotal < ACCENT_MIN_SHARE) break;
    if (peaks.every((p) => circular(centre(k), centre(p)) >= FAMILY_MIN_DEGREES)) peaks.push(k);
  }
  const extra = peaks.slice(secondIsBrand ? 2 : 1).map((k) => dominant(family(k))!);

  let ink = dominant(family(first))!;
  if (!secondIsBrand) {
    const c = hsvOf(ink);
    // Opposite-ish hue, bright enough to FILL a button against the ink.
    const accent = fromHsv((c.h + 165) % 360, Math.min(0.9, Math.max(0.55, c.s)), 0.85);
    return { ink, accent, ground: groundFor(ink), accentDerived: true, chromatic: true, colours: [ink, ...extra] };
  }

  let accent = dominant(family(second))!;
  // 🔑 The ink WRITES, so it should be the darker brand colour. A logo that is
  // mostly gold with a navy wordmark is still navy text on a gold accent.
  if (luminance(ink) > luminance(accent)) [ink, accent] = [accent, ink];
  return { ink, accent, ground: groundFor(ink), accentDerived: false, chromatic: true, colours: [ink, accent, ...extra] };
}
